import { supabase } from '@/integrations/supabase/client';
import { recalculatePlayerBadges } from './pointsService';

export interface GameLike {
  id: string;
  game_id: string;
  player_id: string;
  created_at: string;
}

export interface DeveloperLike {
  id: string;
  developer_id: string;
  player_id: string;
  created_at: string;
}

export interface GameFavorite {
  id: string;
  game_id: string;
  player_id: string;
  created_at: string;
}

// Game likes
export async function likeGame(gameId: string, playerId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('game_likes')
      .insert({
        game_id: gameId,
        player_id: playerId
      });

    if (error) {
      console.error('Error liking game:', error);
      return false;
    }

    // Recalculer les badges en arrière-plan (affecte likes_count)
    recalculatePlayerBadges(playerId).catch(error => {
      console.error('Error recalculating badges after like:', error);
    });

    return true;
  } catch (error) {
    console.error('Error liking game:', error);
    return false;
  }
}

export async function unlikeGame(gameId: string, playerId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('game_likes')
      .delete()
      .eq('game_id', gameId)
      .eq('player_id', playerId);

    if (error) {
      console.error('Error unliking game:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error unliking game:', error);
    return false;
  }
}

export async function isGameLiked(gameId: string, playerId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('game_likes')
      .select('id')
      .eq('game_id', gameId)
      .eq('player_id', playerId)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error checking game like:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error('Error checking game like:', error);
    return false;
  }
}

export async function getGameLikesCount(gameId: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('game_likes')
      .select('*', { count: 'exact', head: true })
      .eq('game_id', gameId);

    if (error) {
      console.error('Error getting game likes count:', error);
      return 0;
    }

    return count || 0;
  } catch (error) {
    console.error('Error getting game likes count:', error);
    return 0;
  }
}

// Developer likes
export async function likeDeveloper(developerId: string, playerId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('developer_likes')
      .insert({
        developer_id: developerId,
        player_id: playerId
      });

    if (error) {
      console.error('Error liking developer:', error);
      return false;
    }

    // Recalculer les badges en arrière-plan (affecte social_actions)
    recalculatePlayerBadges(playerId).catch(error => {
      console.error('Error recalculating badges after developer like:', error);
    });

    return true;
  } catch (error) {
    console.error('Error liking developer:', error);
    return false;
  }
}

export async function unlikeDeveloper(developerId: string, playerId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('developer_likes')
      .delete()
      .eq('developer_id', developerId)
      .eq('player_id', playerId);

    if (error) {
      console.error('Error unliking developer:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error unliking developer:', error);
    return false;
  }
}

export async function isDeveloperLiked(developerId: string, playerId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('developer_likes')
      .select('id')
      .eq('developer_id', developerId)
      .eq('player_id', playerId)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error checking developer like:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error('Error checking developer like:', error);
    return false;
  }
}

export async function getDeveloperLikesCount(developerId: string): Promise<number> {
  try {
    const { count, error } = await supabase
      .from('developer_likes')
      .select('*', { count: 'exact', head: true })
      .eq('developer_id', developerId);

    if (error) {
      console.error('Error getting developer likes count:', error);
      return 0;
    }

    return count || 0;
  } catch (error) {
    console.error('Error getting developer likes count:', error);
    return 0;
  }
}

// Game favorites
export async function addGameToFavorites(gameId: string, playerId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('game_favorites')
      .insert({
        game_id: gameId,
        player_id: playerId
      });

    if (error) {
      console.error('Error adding game to favorites:', error);
      return false;
    }

    // Recalculer les badges en arrière-plan (affecte social_actions)
    recalculatePlayerBadges(playerId).catch(error => {
      console.error('Error recalculating badges after favorite:', error);
    });

    return true;
  } catch (error) {
    console.error('Error adding game to favorites:', error);
    return false;
  }
}

export async function removeGameFromFavorites(gameId: string, playerId: string): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('game_favorites')
      .delete()
      .eq('game_id', gameId)
      .eq('player_id', playerId);

    if (error) {
      console.error('Error removing game from favorites:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error removing game from favorites:', error);
    return false;
  }
}

export async function isGameFavorited(gameId: string, playerId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('game_favorites')
      .select('id')
      .eq('game_id', gameId)
      .eq('player_id', playerId)
      .single();

    if (error && error.code !== 'PGRST116') {
      console.error('Error checking game favorite:', error);
      return false;
    }

    return !!data;
  } catch (error) {
    console.error('Error checking game favorite:', error);
    return false;
  }
}

export async function getPlayerFavorites(playerId: string): Promise<GameFavorite[]> {
  try {
    const { data, error } = await supabase
      .from('game_favorites')
      .select(`
        *,
        game:games(
          id,
          title,
          cover_image_url,
          developer:profiles(display_name, studio_name)
        )
      `)
      .eq('player_id', playerId)
      .order('created_at', { ascending: false });

    if (error) {
      console.error('Error getting player favorites:', error);
      return [];
    }

    return data || [];
  } catch (error) {
    console.error('Error getting player favorites:', error);
    return [];
  }
}
